import * as THREE from "three";
import { TILE } from "../config/constants.js";
import { buildProp } from "./props.js";
import { instancedTiles, fitToHeight } from "../core/modelFit.js";

const LIGHT_SHAPES = { torch: 0xffb060, candle: 0xffcf6f, lamp: 0xffd27f };
const MAX_LIGHTS = 12;
const DOOR_LIFT = 2.6;   // how far a portcullis rises when opened

/**
 * LevelBuilder turns any Level + Theme into 3D geometry. It is elevation-aware:
 * every floor tile, wall, torch, banner and prop is placed at its cell's
 * elevY, and vertical "skirts" fill the faces between stepped heights so the
 * multi-story dungeon reads as a solid structure. Breakable props are tracked
 * for combat; doors are tracked so the Game can open them (V).
 */
export class LevelBuilder {
  constructor(scene, registry) {
    this.scene = scene;
    this.registry = registry;
    this.group = new THREE.Group();
    scene.add(this.group);
    this.portalPos = new THREE.Vector3();
    this.destructibles = [];
    this.doors = [];
    this.lockedChests = [];
  }

  clear() {
    this.group.traverse((o) => {
      if (o.isMesh || o.isInstancedMesh) {
        o.geometry?.dispose?.();
        if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
        else o.material?.dispose?.();
      }
    });
    this.group.clear();
  }

  _elevFn(level) { return (gx, gy) => (level.elevY[gy] ? level.elevY[gy][gx] : 0); }

  build(level, theme) {
    this.clear();
    this._lightCount = 0;
    this.destructibles = [];
    this.doors = [];
    this.lockedChests = [];
    this._plainEdges = [];
    if (!theme.customGround) this._buildFloors(level, theme);   // hub lays its own hex-tile map
    this._buildBoundary(level, theme);
    this._buildElevationSkirts(level);
    if (theme.wallTorch && this.registry.has(theme.wallTorch)) this._buildWallTorches(level, theme);
    if (theme.wallBanner && this.registry.has(theme.wallBanner)) this._buildWallBanners(level, theme);
    this._buildDoors(level, theme);
    this._buildProps(level, theme);
    const e = level.exitWorld();
    this.portalPos.set(e.x, level.groundY(e.x, e.z), e.z);
  }

  _availableModels(keys) { return (keys || []).filter((k) => this.registry.has(k)); }

  // weighted bucket index: first model is the majority
  _variantIndex(count) {
    if (count <= 1) return 0;
    const r = Math.random();
    if (r < 0.14) return 1;
    if (count > 2 && r < 0.22) return 2;
    return 0;
  }

  _buildFloors(level, theme) {
    const cells = [];
    for (let y = 0; y < level.gridH; y++)
      for (let x = 0; x < level.gridW; x++)
        if (level.floor[y][x] === 1) cells.push([x, y]);

    const elevFn = this._elevFn(level);
    const models = this._availableModels(theme.floorModels || (theme.ground.modelKey ? [theme.ground.modelKey] : []));
    if (models.length) {
      const groups = models.map(() => []);
      for (const c of cells) groups[this._variantIndex(models.length)].push(c);
      for (let i = 0; i < models.length; i++) {
        if (!groups[i].length) continue;
        const inst = instancedTiles(this.registry.instance(models[i]), groups[i], TILE, elevFn);
        if (inst) this.group.add(inst);
      }
      return;
    }

    // placeholder two-tone floor
    const geo = new THREE.BoxGeometry(TILE, 0.4, TILE);
    const [cA, cB] = theme.ground.colors;
    const mA = new THREE.MeshStandardMaterial({ color: cA, roughness: 0.95 });
    const mB = new THREE.MeshStandardMaterial({ color: cB, roughness: 0.95 });
    const iA = new THREE.InstancedMesh(geo, mA, cells.length);
    const iB = new THREE.InstancedMesh(geo, mB, cells.length);
    iA.receiveShadow = iB.receiveShadow = true;
    const m = new THREE.Matrix4();
    let a = 0, b = 0;
    for (const [x, y] of cells) {
      m.setPosition(x * TILE, -0.2 + elevFn(x, y), y * TILE);
      if ((x + y) % 2 === 0) iA.setMatrixAt(a++, m); else iB.setMatrixAt(b++, m);
    }
    iA.count = a; iB.count = b;
    iA.instanceMatrix.needsUpdate = iB.instanceMatrix.needsUpdate = true;
    this.group.add(iA, iB);
  }

  _wallEdges(level) {
    const edges = [];
    const H = TILE / 2;
    for (let y = 0; y < level.gridH; y++)
      for (let x = 0; x < level.gridW; x++) {
        if (level.floor[y][x] !== 1) continue;
        const wx = x * TILE, wz = y * TILE, ey = level.elevY[y][x];
        if (!level.hasFloorGrid(x, y - 1)) edges.push([wx, wz - H, 0, 0, 1, ey]);
        if (!level.hasFloorGrid(x, y + 1)) edges.push([wx, wz + H, Math.PI, 0, -1, ey]);
        if (!level.hasFloorGrid(x - 1, y)) edges.push([wx - H, wz, Math.PI / 2, 1, 0, ey]);
        if (!level.hasFloorGrid(x + 1, y)) edges.push([wx + H, wz, -Math.PI / 2, -1, 0, ey]);
      }
    return edges;
  }

  _buildBoundary(level, theme) {
    const bd = theme.boundary;
    const wallModels = this._availableModels(theme.wallModels || (bd.modelKey ? [bd.modelKey] : []));
    if (bd.shape === "wall" && wallModels.length) {
      const edges = this._wallEdges(level);
      if (edges.length) {
        const groups = wallModels.map(() => []);
        for (const e of edges) groups[this._variantIndex(wallModels.length)].push(e);
        this._plainEdges = groups[0] || []; // solid walls (index 0) — torches/banners go here only
        for (let i = 0; i < wallModels.length; i++)
          if (groups[i].length) this._instanceWalls(wallModels[i], groups[i]);
        return;
      }
    }

    // placeholder fence/cliff/wall blocks
    const cells = [];
    for (let y = 0; y < level.gridH; y++)
      for (let x = 0; x < level.gridW; x++)
        if (level.isBoundary(x, y)) cells.push([x, y]);
    if (!cells.length) return;
    const isFence = bd.shape === "fence";
    const h = bd.height;
    const geo = isFence ? new THREE.BoxGeometry(TILE, h, 0.25) : new THREE.BoxGeometry(TILE, h, TILE);
    const material = new THREE.MeshStandardMaterial({ color: bd.color, roughness: 0.9, flatShading: bd.shape === "cliff" });
    const inst = new THREE.InstancedMesh(geo, material, cells.length);
    inst.castShadow = inst.receiveShadow = true;
    const m = new THREE.Matrix4();
    let i = 0;
    for (const [x, y] of cells) {
      const yy = bd.shape === "cliff" ? h / 2 - 0.6 : h / 2 - 0.2;
      m.setPosition(x * TILE, yy, y * TILE);
      inst.setMatrixAt(i++, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    this.group.add(inst);
  }

  // Instance one wall model across a set of oriented, elevation-tagged edges.
  _instanceWalls(key, edges) {
    const src = this.registry.instance(key);
    src.updateWorldMatrix(true, true);
    let mesh = null;
    src.traverse((o) => { if (!mesh && o.isMesh) mesh = o; });
    if (!mesh) return;
    const geo = mesh.geometry.clone();
    geo.applyMatrix4(mesh.matrixWorld);
    geo.computeBoundingBox();
    const bb = geo.boundingBox;
    const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
    geo.translate(-cx, -bb.min.y, -cz);
    geo.computeBoundingBox();
    const width = geo.boundingBox.max.x - geo.boundingBox.min.x || TILE;
    const scale = TILE / width;
    const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const inst = new THREE.InstancedMesh(geo, material, edges.length);
    inst.castShadow = inst.receiveShadow = true;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), scl = new THREE.Vector3(scale, scale, scale), pos = new THREE.Vector3();
    let i = 0;
    // Shift each wall OUTWARD (opposite the inward normal nx,nz) so its inner
    // face lines up with the cell boundary instead of protruding into the
    // walkable floor cell — otherwise the player can stand "inside" the wall.
    const OUT = 0.15;
    for (const [wx, wz, rot, nx, nz, ey] of edges) {
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rot);
      pos.set(wx - (nx || 0) * OUT, ey || 0, wz - (nz || 0) * OUT);
      m.compose(pos, q, scl);
      inst.setMatrixAt(i++, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    this.group.add(inst);
  }

  // Vertical filler faces between a raised cell and lower/empty neighbours, so
  // stepped stories & staircases look solid (no floating tiles or gaps).
  _buildElevationSkirts(level) {
    const specs = [];
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (let y = 0; y < level.gridH; y++)
      for (let x = 0; x < level.gridW; x++) {
        if (level.floor[y][x] !== 1) continue;
        const h = level.elevY[y][x];
        if (h < 0.05) continue;
        for (const [dx, dy] of dirs) {
          const nx = x + dx, ny = y + dy;
          const nFloor = level.hasFloorGrid(nx, ny);
          const base = nFloor ? level.elevY[ny][nx] : 0;
          if (base >= h - 0.05) continue;
          specs.push({
            x: x * TILE + dx * (TILE / 2), y: (base + h) / 2, z: y * TILE + dy * (TILE / 2),
            sx: dx !== 0 ? 0.36 : TILE, sy: h - base, sz: dx !== 0 ? TILE : 0.36,
          });
        }
      }
    if (!specs.length) return;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({ color: 0x2b3038, roughness: 0.95 });
    const inst = new THREE.InstancedMesh(box, mat, specs.length);
    inst.castShadow = false; inst.receiveShadow = true;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(), p = new THREE.Vector3();
    specs.forEach((sp, i) => { p.set(sp.x, sp.y, sp.z); s.set(sp.sx, sp.sy, sp.sz); m.compose(p, q, s); inst.setMatrixAt(i, m); });
    inst.instanceMatrix.needsUpdate = true;
    this.group.add(inst);
  }

  _buildWallTorches(level, theme) {
    const edges = (this._plainEdges && this._plainEdges.length) ? this._plainEdges : this._wallEdges(level);
    const step = 5;
    const scale = (theme.modelScale || 1) * 1.2;
    for (let i = 0; i < edges.length; i += step) {
      if (this._lightCount >= MAX_LIGHTS) break;
      const [wx, wz, rot, nx, nz, ey = 0] = edges[i];
      const torch = this.registry.instance(theme.wallTorch);
      torch.scale.setScalar(scale);
      torch.rotation.y = rot;
      torch.position.set(wx + nx * 0.2, ey + 1.4, wz + nz * 0.2);
      torch.traverse((o) => { if (o.isMesh) o.castShadow = false; });
      this.group.add(torch);
      const L = new THREE.PointLight(0xffa844, 1.8, 12, 1.5);
      L.position.set(wx + nx * 0.5, ey + 1.7, wz + nz * 0.5);
      this.group.add(L);
      this._lightCount++;
    }
  }

  // Hanging banners flush against solid wall faces (mid-wall height).
  _buildWallBanners(level, theme) {
    const edges = (this._plainEdges && this._plainEdges.length) ? this._plainEdges : this._wallEdges(level);
    const step = 9;
    const scale = (theme.modelScale || 1) * 0.9;
    for (let i = 2; i < edges.length; i += step) {
      const [wx, wz, rot, nx, nz, ey = 0] = edges[i];
      const b = this.registry.instance(theme.wallBanner);
      b.scale.setScalar(scale);
      b.rotation.y = rot;
      // anchor by bounding box so the banner's TOP hangs just below the wall top,
      // regardless of the model's pivot (fixes "floating too high").
      b.updateWorldMatrix(true, true);
      const bb = new THREE.Box3().setFromObject(b);
      const topTarget = ey + 1.7;
      b.position.set(wx + (nx || 0) * 0.08, topTarget - bb.max.y, wz + (nz || 0) * 0.08);
      b.traverse((o) => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = true; } });
      this.group.add(b);
    }
  }

  // Fit a single model so its footprint fills one tile, feet at baseY.
  _fitTile(o, wx, wz, rot, baseY) {
    o.rotation.y = rot;
    o.updateWorldMatrix(true, true);
    let b = new THREE.Box3().setFromObject(o); const s = new THREE.Vector3(); b.getSize(s);
    const fp = Math.max(s.x, s.z) || 1; o.scale.multiplyScalar(TILE / fp);
    o.updateWorldMatrix(true, true);
    b = new THREE.Box3().setFromObject(o);
    o.position.set(wx, baseY - b.min.y, wz);
    return o;
  }

  _buildDoors(level, theme) {
    const key = theme.doorKey;
    for (const d of (level.doors || [])) {
      const grp = new THREE.Group();
      for (const [cx, cy] of d.cells) {
        let seg;
        if (key && this.registry.has(key)) {
          seg = this.registry.instance(key);
          this._fitTile(seg, cx * TILE, cy * TILE, d.rot, d.elevY);
        } else {
          seg = new THREE.Mesh(
            new THREE.BoxGeometry(TILE * 0.9, 2.4, 0.25),
            new THREE.MeshStandardMaterial({ color: 0x6b6f77, metalness: 0.6, roughness: 0.5 })
          );
          seg.rotation.y = d.rot;
          seg.position.set(cx * TILE, d.elevY + 1.2, cy * TILE);
        }
        seg.traverse((o) => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = true; } });
        grp.add(seg);
      }
      if (d.locked) {
        // gold "lock" bar so the sealed vault door reads as key-gated
        const lc = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 0),
          new THREE.MeshStandardMaterial({ color: 0xf0c34b, emissive: 0xf0c34b, emissiveIntensity: 1.0, metalness: 0.7, roughness: 0.3 }));
        lc.position.set(d.wx * TILE, (d.elevY || 0) + 1.3, d.wz * TILE);
        grp.add(lc);
      }
      this.group.add(grp);
      this.doors.push({ mesh: grp, cells: d.cells, x: d.wx * TILE, z: d.wz * TILE, open: false, opening: false, openY: DOOR_LIFT,
        locked: !!d.locked, keyCost: d.keyCost || 0, tier: d.tier || null, vault: !!d.vault });
    }
  }

  _buildProps(level, theme) {
    const modelScale = theme.modelScale || 1;
    const batches = new Map();   // model key -> [props]  (static decor drawn as one InstancedMesh)
    for (const p of level.props) {
      const gx = Math.floor(p.gx), gy = Math.floor(p.gy);
      const ey = (level.elevY[gy] ? level.elevY[gy][gx] : 0) + (p.y || 0);
      const w = level.gridToWorld(p.gx, p.gy);
      const isLight = LIGHT_SHAPES[p.shape] !== undefined;
      const hasModel = p.key && this.registry.has(p.key);

      // Batch static decor (trees, rocks, grass, bushes, pillars…): not breakable,
      // not a light source, and backed by a real model. Everything else is
      // built individually because it needs a live reference (destructibles) or
      // emits a light.
      // batch static decor (not breakable, not a light, not fit-scaled, not a
      // locked chest — locked chests need a live ref for the hinged lid)
      if (hasModel && !p.breakable && !isLight && !p.fit && !p.locked) {
        if (!batches.has(p.key)) batches.set(p.key, []);
        batches.get(p.key).push({ x: w.x, y: ey, z: w.z, s: modelScale * (p.scale || 1), rot: p.rot || 0 });
        continue;
      }

      const obj = hasModel ? this.registry.instance(p.key) : buildProp(p);
      if (hasModel) {
        if (p.fit) fitToHeight(obj, p.fit); else obj.scale.setScalar(modelScale * (p.scale || 1));   // fit → scale to a target height (unknown native size)
        obj.rotation.y = p.rot || 0;
        obj.traverse((o) => { if (o.isMesh) { o.castShadow = !!p.fit; o.receiveShadow = true; } });
      }
      if (hasModel && p.fit) { obj.position.x = w.x; obj.position.z = w.z; obj.position.y += ey; }   // keep fit's feet-drop
      else obj.position.set(w.x, ey, w.z);
      this.group.add(obj);

      if (p.locked) {
        // find the hinged lid node so the Game can swing it open on unlock
        let lid = null;
        if (p.lidNode) obj.traverse((o) => { if (o.name === p.lidNode) lid = o; });
        const tierCol = { bronze: 0xc8834a, silver: 0xd7dde3, gold: 0xf0c34b }[p.tier] || 0xf0c34b;
        // a small floating emissive "lock" so locked chests read at a glance
        const lock = new THREE.Mesh(new THREE.IcosahedronGeometry(0.14, 0),
          new THREE.MeshStandardMaterial({ color: tierCol, emissive: tierCol, emissiveIntensity: 0.9, metalness: 0.6, roughness: 0.3 }));
        lock.position.set(w.x, ey + 0.75, w.z);
        this.group.add(lock);
        this.lockedChests.push({ obj, lid, lock, gx, gy, x: w.x, z: w.z, tier: p.tier, keyCost: p.keyCost, mimic: !!p.mimic, unlocked: false, contents: null, t: Math.random() * 6.28 });
      }
      if (p.breakable) this.destructibles.push({ obj, gx, gy, x: w.x, z: w.z, table: p.table || "barrel", broken: false });
      if (isLight && this._lightCount < MAX_LIGHTS) {
        const light = new THREE.PointLight(LIGHT_SHAPES[p.shape], 1.4, 9, 1.6);
        light.position.set(w.x, ey + 1.6, w.z);
        this.group.add(light);
        this._lightCount++;
      }
    }
    for (const [key, list] of batches) this._batchProps(key, list);
  }

  // Draw many copies of one decor model as InstancedMesh(es) — one per submesh.
  _batchProps(key, placements) {
    const src = this.registry.instance(key);
    src.updateWorldMatrix(true, true);
    const meshes = [];
    src.traverse((o) => { if (o.isMesh) meshes.push(o); });
    if (!meshes.length) return;

    const q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0), pos = new THREE.Vector3(), scl = new THREE.Vector3();
    const model = new THREE.Matrix4();
    for (const mesh of meshes) {
      const geo = mesh.geometry.clone();
      geo.applyMatrix4(mesh.matrixWorld);           // bake this submesh's local transform
      const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      const inst = new THREE.InstancedMesh(geo, material, placements.length);
      inst.castShadow = false; inst.receiveShadow = true;
      const m = new THREE.Matrix4();
      placements.forEach((p, i) => {
        q.setFromAxisAngle(up, p.rot); pos.set(p.x, p.y, p.z); scl.set(p.s, p.s, p.s);
        m.compose(pos, q, scl); inst.setMatrixAt(i, m);
      });
      inst.instanceMatrix.needsUpdate = true;
      this.group.add(inst);
    }
  }
}
